import { Component, EventEmitter, Input, OnInit, Output, Pipe, PipeTransform } from '@angular/core';
import { Router } from '@angular/router';
import {
  CUSTOM_SUBSCRIPTION_ID,
  ENTERPRISE_SUBSCRIPTION_ID,
  SubscriptionTiersService,
  VBP2_STARTUP_SUBSCRIPTION_ID,
  VBP_BASIC_SUBSCRIPTION_ID,
  VBP_ENTERPRISE_PILOT_SUBSCRIPTION_ID,
  VBP_ENTERPRISE_SUBSCRIPTION_ID,
  VBP_PROFESSIONAL_SUBSCRIPTION_ID,
} from '@galaxy/partner';
import { Observable, zip } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { SupportNumber } from '../common/support_phone_number';
import { PartnerService } from '../core/partner.service';
import { TrialStatus } from '../core/partner/partner-trial-status';
import { PartnerTrialStoreService } from '../partner-trial';
import { FeatureInformation } from './access-restricted-content';

@Component({
  selector: 'app-access-restricted-content',
  template: `
    <div class="restricted-feature">
      <div class="feature-title">
        <div *ngIf="featureInfo.featureIcon" class="icon">
          <mat-icon *ngIf="!featureInfo.featureIcon.startsWith('ico-'); else svgIcon">
            {{ featureInfo.featureIcon }}
          </mat-icon>
          <ng-template #svgIcon>
            <img
              [src]="
                'https://vstatic-prod.apigateway.co/partner-center-client.9223372035140627030.prod/assets/images/icons/' +
                featureInfo.featureIcon +
                '.svg'
              "
            />
          </ng-template>
        </div>
        <div class="title">
          <h1>{{ featureInfo.featureTitle | translate }}</h1>
          <h4>{{ featureInfo.minUpgradeLevel | availableSubscriptions }}</h4>
        </div>
      </div>
      <div class="feature-info">
        <h3 *ngIf="featureInfo.shortDescription">{{ featureInfo.shortDescription | translate }}</h3>
        <p *ngFor="let para of featureInfo.longDescriptionParagraphs">{{ para | translate }}</p>
      </div>
      <div class="contact-upgrade">
        <p *ngIf="supportNumber$ | async as phone">
          <a [href]="instantDemoUrl" (click)="trackUserEvent('instant-demo-clicked')">
            {{ 'RESTRICTED_FEATURE_CONTENT.GET_AN_INSTANT_DEMO' | translate }}
          </a>
          {{ 'RESTRICTED_FEATURE_CONTENT.OR_CALL_US' | translate }}:
          <a href="tel:{{ phone }}" (click)="trackUserEvent('phone-number-clicked')">
            {{ phone }}
          </a>
        </p>
        <a
          mat-raised-button
          color="primary"
          *ngIf="isFreeTier$ | async"
          (click)="trackUserEvent('upgrade-clicked')"
          [routerLink]="['/upgrade-subscription-tier']"
          [queryParams]="{ showUpgradeDialog: true }"
        >
          {{ 'RESTRICTED_FEATURE_CONTENT.UPGRADE' | translate }}
        </a>
      </div>
    </div>
  `,
  styleUrls: ['./access-restricted-content.component.scss'],
})
export class AccessRestrictedContentComponent implements OnInit {
  @Input() featureInfo: FeatureInformation;
  @Input() userEventCategory: string;
  @Output() ctaClicked: EventEmitter<string> = new EventEmitter();

  instantDemoUrl =
    'https://www.vendasta.com/get-an-instant-demo?utm_source=partner-center&utm_medium=referral&utm_campaign=upgrade-to-starter&utm_content=instant-demo-button';

  supportNumber$: Observable<string>;
  trialActive$: Observable<boolean>;
  isFreeTier$: Observable<boolean>;

  constructor(
    private router: Router,
    private partnerService: PartnerService,
    private trialService: PartnerTrialStoreService,
  ) {}

  ngOnInit(): void {
    this.trialActive$ = this.trialService.partnerTrial$.pipe(map((t) => t.status === TrialStatus.ACTIVE));
    this.isFreeTier$ = this.partnerService.subscriptionTier$.pipe(map((s) => SubscriptionTiersService.isFree(s)));

    this.supportNumber$ = zip(this.partnerService.subscriptionTier$, this.trialActive$).pipe(
      take(1),
      map(([subscriptionTier, onTrial]) => {
        if (
          onTrial ||
          subscriptionTier.id === VBP_BASIC_SUBSCRIPTION_ID ||
          subscriptionTier.id === VBP_PROFESSIONAL_SUBSCRIPTION_ID
        ) {
          return SupportNumber.TRIAL_SUPPORT_NUMBER;
        }
        return SupportNumber.BDR_NUMBER;
      }),
    );
  }

  handleCtaClick(cta: { link?: string; onClick?: () => void }): void {
    if (cta.onClick) {
      cta.onClick.call(this);
    }
    if (cta.link) {
      this.router.navigateByUrl(cta.link);
    }
  }

  trackUserEvent(action: string): void {
    this.ctaClicked.emit(action);
  }
}

@Pipe({ name: 'availableSubscriptions' })
export class AvailableSubscriptionsPipe implements PipeTransform {
  constructor(
    private readonly subscriptionTiersService: SubscriptionTiersService,
    private readonly translateService: TranslateService,
  ) {}

  transform(tierId: string): string {
    const name = this.translateService.instant('SUBSCRIPTION_TIERS.' + tierId + '.NAME');
    if (
      tierId === ENTERPRISE_SUBSCRIPTION_ID ||
      tierId === VBP_ENTERPRISE_SUBSCRIPTION_ID ||
      tierId === VBP2_STARTUP_SUBSCRIPTION_ID ||
      tierId === VBP_ENTERPRISE_PILOT_SUBSCRIPTION_ID ||
      tierId === CUSTOM_SUBSCRIPTION_ID
    ) {
      return `Available on ${name} subscription`;
    }
    return `Available on ${name} subscriptions and up`;
  }
}
